import type { ReactNode } from 'react';
import { lazy, Suspense } from 'react';
import type { RouteObject } from 'react-router-dom';
import { Navigate, Outlet } from 'react-router-dom';
import { RequireAuth } from '@/auth/RequireAuth';
import { Spinner } from '@/components/ui';
import { AppLayout } from '@/layout/AppLayout';
import { PublicLayout } from '@/layout/PublicLayout';
import type { CaseTab } from '@/pages/cases/tabs';
import { CASE_TABS } from '@/pages/cases/tabs';

// Every screen is its own chunk; layouts are in the initial bundle.
const CasesPage = lazy(() => import('@/pages/cases/CasesPage'));
const NewCasePage = lazy(() => import('@/pages/cases/NewCasePage'));
const CaseLayout = lazy(() => import('@/pages/cases/CaseLayout'));
const OverviewPage = lazy(() => import('@/features/overview/OverviewPage'));
const FindingsPage = lazy(() => import('@/features/findings/FindingsPage'));
const DocumentsPage = lazy(() => import('@/features/documents/DocumentsPage'));
const PathwayPage = lazy(() => import('@/features/pathway/PathwayPage'));
const ReportPage = lazy(() => import('@/features/report/ReportPage'));
const MethodologyLayout = lazy(() => import('@/features/admin/MethodologyLayout'));
const QuestionLibrary = lazy(() => import('@/features/admin/QuestionLibrary'));
const PackList = lazy(() => import('@/features/admin/PackList'));
const PackDetailPage = lazy(() => import('@/features/admin/PackDetailPage'));
const GlossaryPage = lazy(() => import('@/features/admin/GlossaryPage'));
const FundersPage = lazy(() => import('@/features/admin/FundersPage'));
const UsagePage = lazy(() => import('@/features/admin/UsagePage'));
const LoginPage = lazy(() => import('@/pages/auth/LoginPage'));
const AuthCallbackPage = lazy(() => import('@/pages/auth/AuthCallbackPage'));
const SharePage = lazy(() => import('@/pages/SharePage'));
const NotFoundPage = lazy(() => import('@/pages/NotFoundPage'));

// Every case tab with its screen.
const TAB_PAGES: Record<CaseTab, ReactNode> = {
  overview: <OverviewPage />,
  documents: <DocumentsPage />,
  findings: <FindingsPage />,
  pathway: <PathwayPage />,
  report: <ReportPage />,
};

const children: RouteObject[] = [
  {
    // Public: no sign-in, no sidebar.
    element: <PublicLayout />,
    children: [
      { path: '/share/:token', element: <SharePage /> },
      { path: '/login', element: <LoginPage /> },
      { path: '/auth/callback', element: <AuthCallbackPage /> },
    ],
  },
  {
    element: (
      <RequireAuth>
        <AppLayout />
      </RequireAuth>
    ),
    children: [
      { path: '/', element: <Navigate to="/cases" replace /> },
      { path: '/cases', element: <CasesPage /> },
      { path: '/cases/new', element: <NewCasePage /> },
      {
        path: '/cases/:projectId',
        element: <CaseLayout />,
        children: [
          { index: true, element: <Navigate to="overview" replace /> },
          ...CASE_TABS.map((tab) => ({
            path: tab,
            element: TAB_PAGES[tab],
          })),
        ],
      },
      { path: '/admin', element: <Navigate to="/admin/methodology" replace /> },
      {
        path: '/admin/methodology',
        element: <MethodologyLayout />,
        children: [
          { index: true, element: <Navigate to="questions" replace /> },
          { path: 'questions', element: <QuestionLibrary /> },
          { path: 'packs', element: <PackList /> },
          { path: 'packs/:packId', element: <PackDetailPage /> },
          { path: 'glossary', element: <GlossaryPage /> },
        ],
      },
      { path: '/admin/funders', element: <FundersPage /> },
      { path: '/admin/usage', element: <UsagePage /> },
    ],
  },
  { path: '*', element: <NotFoundPage /> },
];

/** One root so every lazy screen, layouts or not, has a Suspense boundary. */
export const routes: RouteObject[] = [
  {
    element: (
      <Suspense fallback={<Spinner className="m-8" />}>
        <Outlet />
      </Suspense>
    ),
    children,
  },
];
